import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Input, Modal, Select, Space, Switch, Table, Tag, Tooltip, Typography, message } from 'antd';
import { CopyOutlined, KeyOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import api from '../services/api';
import { useAuthGuard } from '../hooks/useAuthGuard';
import { useSidebar } from '../contexts/SidebarContext';
import { useNewGui } from '../hooks/useNewGui';
import PageHeader from '../components/shared/PageHeader';

const { Text } = Typography;

export interface PmaUser {
  id: number;
  sid: string;
  email: string;
  name?: string;
  method: 'local' | 'google' | 'github';
  role?: string;
  isVerified: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const ROLE_OPTIONS = [
  { value: 'itoperator', label: 'IT Operator' },
  { value: 'itadmin', label: 'IT Admin' },
  { value: 'observer', label: 'Observer' },
  { value: 'auditor', label: 'Auditor' },
  { value: 'approver', label: 'Approver' },
  { value: 'superadmin', label: 'Superadmin' },
];

const errMsg = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

export default function UserManagementPage() {
  const { user } = useAuthGuard();
  const { isSidebarOpen } = useSidebar();
  const useNew = useNewGui();

  const [users, setUsers] = useState<PmaUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingSid, setSavingSid] = useState<string | null>(null);

  const [resetTarget, setResetTarget] = useState<PmaUser | null>(null);
  const [resetting, setResetting] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<PmaUser[]>('/users');
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      message.error(errMsg(e, 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRoleChange = async (target: PmaUser, role: string) => {
    setSavingSid(target.sid);
    try {
      const { data } = await api.put<PmaUser>(`/users/${target.sid}/role`, { role });
      setUsers(prev => prev.map(u => (u.sid === target.sid ? { ...u, role: data.role } : u)));
      message.success(`Role updated for ${target.email}`);
    } catch (e) {
      message.error(errMsg(e, 'Failed to update role'));
    } finally {
      setSavingSid(null);
    }
  };

  const handleActiveToggle = (target: PmaUser, isActive: boolean) => {
    const doToggle = async () => {
      setSavingSid(target.sid);
      try {
        const { data } = await api.put<PmaUser>(`/users/${target.sid}/status`, { isActive });
        setUsers(prev => prev.map(u => (u.sid === target.sid ? { ...u, isActive: data.isActive } : u)));
        message.success(`${target.email} ${isActive ? 'enabled' : 'disabled'}`);
      } catch (e) {
        message.error(errMsg(e, 'Failed to update status'));
      } finally {
        setSavingSid(null);
      }
    };

    if (!isActive) {
      Modal.confirm({
        title: 'Disable this user?',
        content: `${target.email} will be immediately signed out and unable to log in until re-enabled.`,
        okText: 'Disable',
        okType: 'danger',
        onOk: doToggle,
      });
    } else {
      doToggle();
    }
  };

  const openResetPassword = (target: PmaUser) => {
    setResetTarget(target);
    setTempPassword(null);
  };

  const confirmResetPassword = async () => {
    if (!resetTarget) return;
    setResetting(true);
    try {
      const { data } = await api.post<{ temporaryPassword: string }>(`/users/${resetTarget.sid}/reset-password`);
      setTempPassword(data.temporaryPassword);
    } catch (e) {
      message.error(errMsg(e, 'Failed to reset password'));
      setResetTarget(null);
    } finally {
      setResetting(false);
    }
  };

  const closeResetModal = () => {
    setResetTarget(null);
    setTempPassword(null);
  };

  const copyPassword = () => {
    if (tempPassword) {
      navigator.clipboard.writeText(tempPassword);
      message.success('Copied to clipboard');
    }
  };

  const columns: ColumnsType<PmaUser> = [
    { title: 'Email', dataIndex: 'email', key: 'email', sorter: (a, b) => a.email.localeCompare(b.email), ellipsis: true },
    { title: 'Name', dataIndex: 'name', key: 'name', ellipsis: true, render: v => v || '—' },
    { title: 'Method', dataIndex: 'method', key: 'method', width: 90, render: v => <Tag>{v}</Tag> },
    {
      title: 'Role', dataIndex: 'role', key: 'role', width: 170,
      render: (role, record) => {
        const isSelf = record.email === user?.email;
        return (
          <Tooltip title={isSelf ? "You can't change your own role" : undefined}>
            <Select
              size="small"
              style={{ width: 150 }}
              value={role || undefined}
              placeholder="No role"
              disabled={isSelf || savingSid === record.sid}
              options={ROLE_OPTIONS}
              onChange={(v) => handleRoleChange(record, v)}
            />
          </Tooltip>
        );
      },
    },
    {
      title: 'Verified', dataIndex: 'isVerified', key: 'isVerified', width: 90,
      render: v => (v ? <Tag color="green">Yes</Tag> : <Tag color="default">No</Tag>),
    },
    {
      title: 'Status', dataIndex: 'isActive', key: 'isActive', width: 110,
      render: (isActive, record) => {
        const isSelf = record.email === user?.email;
        return (
          <Tooltip title={isSelf ? "You can't disable your own account" : undefined}>
            <Space size="small">
              <Switch
                size="small"
                checked={isActive}
                disabled={isSelf || savingSid === record.sid}
                onChange={(checked) => handleActiveToggle(record, checked)}
              />
              <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Enabled' : 'Disabled'}</Tag>
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: 'Created', dataIndex: 'createdAt', key: 'createdAt', width: 130,
      render: v => (v ? new Date(v).toLocaleDateString() : '—'),
      sorter: (a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? ''),
    },
    {
      title: 'Actions', key: 'actions', width: 150,
      render: (_, record) => (
        <Tooltip title={record.method !== 'local' ? `${record.method} accounts don't have a local password` : undefined}>
          <Button
            size="small"
            icon={<KeyOutlined />}
            disabled={record.method !== 'local'}
            onClick={() => openResetPassword(record)}
          >
            Reset Password
          </Button>
        </Tooltip>
      ),
    },
  ];

  if (!user || user.role !== 'superadmin') {
    return (
      <div className={`flex-1 p-6 transition-all duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-20'}`}>
        <Alert type="error" message="Access denied. Superadmin role required." showIcon />
      </div>
    );
  }

  return (
    <div className={`p-4 ${isSidebarOpen ? 'ml-64' : 'ml-16'} transition-all`}>
      <Card className="shadow">
        {useNew ? (
          <PageHeader
            title="User Administration"
            purpose="Manage PMA application users: roles, account status, and password resets."
            actions={<Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>}
          />
        ) : (
          <div className="flex justify-between items-center mb-4">
            <Typography.Title level={4} className="!mb-0">User Administration</Typography.Title>
            <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
          </div>
        )}

        <Table
          rowKey="sid"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={users}
          pagination={{ pageSize: 20 }}
          scroll={{ x: true }}
        />
      </Card>

      <Modal
        title={`Reset password — ${resetTarget?.email ?? ''}`}
        open={!!resetTarget}
        onCancel={closeResetModal}
        footer={tempPassword ? [
          <Button key="close" type="primary" onClick={closeResetModal}>Done</Button>,
        ] : [
          <Button key="cancel" onClick={closeResetModal}>Cancel</Button>,
          <Button key="reset" type="primary" danger loading={resetting} onClick={confirmResetPassword}>
            Generate New Password
          </Button>,
        ]}
        destroyOnClose
      >
        {tempPassword ? (
          <>
            <Alert
              type="success"
              showIcon
              message="Password reset"
              description="Share this temporary password with the user through a secure channel. It will not be shown again."
              className="mb-3"
            />
            <Space.Compact style={{ width: '100%' }}>
              <Input readOnly value={tempPassword} />
              <Button icon={<CopyOutlined />} onClick={copyPassword} />
            </Space.Compact>
          </>
        ) : (
          <Text>
            This will generate a new random password for <Text strong>{resetTarget?.email}</Text> and invalidate
            their current password immediately. They will need to be given the new password to sign in.
          </Text>
        )}
      </Modal>
    </div>
  );
}
